import { format } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import EventIcon from '@mui/icons-material/Event'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardHeader,
  CardProps,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useMonolithBaseUrl } from '@src/services/monolith-hook'
import { InteracPossibleMatch } from '@src/types/interac'
import { createRef, Fragment, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface InteracWirePossibleMatchProps {
  possibleMatch: InteracPossibleMatch
  variant?: CardProps['variant']
  onSelect: (possibleMatch: InteracPossibleMatch) => void
  disabled?: boolean
}

const InteracWirePossibleMatch = ({ possibleMatch, variant, onSelect, disabled }: InteracWirePossibleMatchProps) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<boolean>(false)
  const cardRef = createRef<HTMLDivElement>()
  const monolithBaseUrl = useMonolithBaseUrl()

  const notes = useMemo(() => {
    return possibleMatch.notes.map((note, index) => (
      <Fragment key={index}>
        {index > 0 && <Divider />}
        <ListItem>
          <ListItemText
            primary={
              <>
                {note.createdByUsername ? `${note.createdByUsername} - ` : ''}
                {format(note.createdOn, 'P')}
              </>
            }
            secondary={note.note}
          />
        </ListItem>
      </Fragment>
    ))
  }, [possibleMatch.notes])

  const handleExpandedChange = useCallback(() => {
    setExpanded((prev) => !prev)
  }, [])

  const handleScroll = useCallback(() => {
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      cardRef.current.focus()
    }
  }, [cardRef])
  return (
    <Card ref={cardRef} variant={variant} style={{ overflow: 'visible' }}>
      <CardHeader
        title={
          <Stack>
            <Typography variant="h6">
              {t(`enum.customerType.${possibleMatch.ppaCustomerTypeName}`)} #{possibleMatch.referenceNumber}
            </Typography>

            <Typography variant="inherit">
              {possibleMatch.applicantFirstName} {possibleMatch.applicantLastName}
            </Typography>
            {possibleMatch.coapplicantFirstName && possibleMatch.coapplicantLastName && (
              <Typography variant="inherit">
                {possibleMatch.coapplicantFirstName} {possibleMatch.coapplicantLastName}
              </Typography>
            )}
            <Typography variant="body1">{possibleMatch.merchantName}</Typography>
          </Stack>
        }
        action={
          <Stack direction="row" spacing={1}>
            <Tooltip title={t('common.details')}>
              <IconButton
                href={`${monolithBaseUrl}/admin/ppa/customer.aspx?id=${possibleMatch.loanId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.calendar')}>
              <IconButton
                href={`${monolithBaseUrl}/admin/customer/${possibleMatch.loanId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <EventIcon />
              </IconButton>
            </Tooltip>
            <Button onClick={() => onSelect(possibleMatch)} disabled={disabled}>
              {t('interacWire.select')}
            </Button>
          </Stack>
        }
        subheader={
          <Stack direction="row" spacing={2}>
            <div>
              <Typography variant="subtitle2" color="textSecondary">
                {t('interacWire.lateAmount')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatCurrency(possibleMatch.lateAmount)}
              </Typography>
            </div>
            <div>
              <Typography variant="subtitle2" color="textSecondary">
                {t('interacWire.balanceAmount')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatCurrency(possibleMatch.loanBalance)}
              </Typography>
            </div>
          </Stack>
        }
      />
      {notes.length > 0 && (
        <Accordion
          expanded={expanded}
          onChange={handleExpandedChange}
          onTransitionEnd={handleScroll}
          disableGutters
          sx={{ border: 'none', boxShadow: 'none', '&::before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={[
              { borderTop: 1, borderColor: 'divider' },
              expanded && {
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                backgroundColor: 'background.paper',
                borderTop: 1,
                borderBottom: 1,
                borderColor: 'divider',
              },
            ]}
          >
            <Typography>{t('interacWire.notes')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>{notes}</List>
          </AccordionDetails>
        </Accordion>
      )}
    </Card>
  )
}

export default InteracWirePossibleMatch
